import { useState, useEffect } from 'react';
import EmailListDisplay from './EmailListDisplay';
import CurrentMonthLeaderboard from './CurrentMonthLeaderboard';
import WeeklyLeaderboard from './WeeklyLeaderboard';
import PatientTypeBreakdown from './PatientTypeBreakdown';
import OrgMonthlyChart from './OrgMonthlyChart';
import InvoiceModal from './InvoiceModal';
import { getOrgDataForMonth, getPatientTypeBreakdown } from '../../../utils/functions';

export default function OrgDetails({ org, setOpenOrgModal, doctorsArr }) {
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });

  const [orgData, setOrgData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showEmails, setShowEmails] = useState(false);
  const [showNames, setShowNames] = useState(false);
  const [showLeaderboard, setShowLeaderboard] = useState("");
  const [openPatientsModal, setOpenPatientsModal] = useState(false);
  const [patientTypes, setPatientTypes] = useState(null);
  const [orgChartModal, setOrgChartModal] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);

  // Generate month options for the past 12 months
  const generateMonthOptions = () => {
    const options = [];
    const now = new Date();

    for (let i = 0; i < 12; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const label = date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
      options.push({ value, label });
    }

    return options;
  };

  // Fetch data when month changes
  useEffect(() => {
    const fetchMonthData = async () => {
      if (!org || !selectedMonth) return;

      setLoading(true);
      try {
        const data = await getOrgDataForMonth(org, selectedMonth);
        setOrgData(data);
      } catch (error) {
        console.error('Error fetching org data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchMonthData();
  }, [org, selectedMonth]);

  const handleMonthChange = (e) => {
    setSelectedMonth(e.target.value);
    resetDisplayStates();
  };

  const resetDisplayStates = () => {
    setShowEmails(false);
    setShowNames(false);
    setShowLeaderboard("");
    setOpenPatientsModal(false);
    setPatientTypes(null);
  };

  const handlePatientTypes = async () => {
    setOpenPatientsModal(true);
    if (!patientTypes) {
      const patientTypeBreakdown = await getPatientTypeBreakdown(org, selectedMonth);
      setPatientTypes(patientTypeBreakdown);
    }
  };

  const formatMonthLabel = (monthString) => {
    const [year, month] = monthString.split('-');
    const date = new Date(year, month - 1);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
  };

  const StatCard = ({ value, label, color = "text-ktx-purple" }) => (
    <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm text-center">
      <div className={`text-3xl font-bold ${color} mb-1`}>{value}</div>
      <div className="text-sm text-gray-600 font-medium">{label}</div>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-[90%] max-w-5xl max-h-[90%] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-ktx-purple">{org}</h2>
            <p className="text-gray-600 text-sm mt-1">Organization Analytics Dashboard</p>
          </div>

          <div className="flex items-center gap-4">
            {/* Month Selector */}
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">Month:</label>
              <select
                value={selectedMonth}
                onChange={handleMonthChange}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-ktx-purple focus:border-transparent text-sm"
              >
                {generateMonthOptions().map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <button
              className='p-2 hover:bg-gray-100 rounded-full transition-colors'
              onClick={() => setOpenOrgModal(false)}
            >
              <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="flex flex-col items-center gap-3">
                <span className="w-8 h-8 border-2 border-dashed rounded-full animate-spin border-ktx-purple"></span>
                <p className="text-gray-600">Loading organization data...</p>
              </div>
            </div>
          ) : orgData ? (
            <div className="space-y-8">
              {/* Key Metrics */}
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Organization Overview</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <StatCard
                    value={orgData.totalDoctors}
                    label="Total Doctors"
                    color="text-ktx-purple"
                  />
                  <StatCard
                    value={orgData.activeDoctors}
                    label="Active Doctors (All Time)"
                    color="text-green-600"
                  />
                  <StatCard
                    value={orgData.totalNotes}
                    label="Total Notes"
                    color="text-blue-600"
                  />
                  <StatCard
                    value={orgData.avgNotesPerDoctor?.toFixed(1) || '0'}
                    label="Avg Notes/Doctor"
                    color="text-orange-600"
                  />
                </div>
              </div>

              {/* Monthly Activity */}
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-4">
                  {formatMonthLabel(selectedMonth)} Activity
                </h3>

                <div className="bg-gray-50 rounded-lg p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                    {/* Active Doctors Section */}
                    <div className="bg-white rounded-lg p-5 shadow-sm border border-gray-200">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h4 className="font-semibold text-gray-800 mb-1">Active Doctors</h4>
                          <p className="text-3xl font-bold text-ktx-purple">{orgData.activeDoctorsThisMonth}</p>
                          <p className="text-sm text-gray-500 mt-1">
                            {orgData.activeDoctorsThisMonth > 0 && orgData.totalDoctors > 0
                              ? `${((orgData.activeDoctorsThisMonth / orgData.totalDoctors) * 100).toFixed(1)}% of total doctors`
                              : 'No active doctors this month'
                            }
                          </p>
                        </div>
                        <div className="flex flex-col gap-2">
                          <button
                            onClick={() => setShowNames(!showNames)}
                            className={`px-3 py-2 text-xs rounded-lg transition-colors font-medium ${showNames
                              ? 'bg-ktx-purple text-white shadow-sm'
                              : 'bg-gray-100 text-gray-700 hover:bg-ktx-purple hover:text-white border border-gray-300'
                              }`}
                          >
                            {showNames ? '👁️ Hide Names' : '👥 Show Names'}
                          </button>
                          <button
                            onClick={() => setShowEmails(!showEmails)}
                            className={`px-3 py-2 text-xs rounded-lg transition-colors font-medium ${showEmails
                              ? 'bg-ktx-purple text-white shadow-sm'
                              : 'bg-gray-100 text-gray-700 hover:bg-ktx-purple hover:text-white border border-gray-300'
                              }`}
                          >
                            {showEmails ? '👁️ Hide Emails' : '📧 Show Emails'}
                          </button>
                        </div>
                      </div>

                      {(showNames || showEmails) && (
                        <div className="space-y-2">
                          {showNames && (
                            <EmailListDisplay
                              nameList={orgData.activeUserNames}
                              setShowPreviousNames={setShowNames}
                              componentName="Names-Prev"
                              monthInQuestion={selectedMonth}
                            />
                          )}
                          {showEmails && (
                            <EmailListDisplay
                              emailList={orgData.activeUserEmails}
                              setShowPreviousEmails={setShowEmails}
                              componentName="Emails-Prev"
                              monthInQuestion={selectedMonth}
                            />
                          )}
                        </div>
                      )}
                    </div>

                    {/* Notes Generated Section */}
                    <div className="bg-white rounded-lg p-5 shadow-sm border border-gray-200">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h4 className="font-semibold text-gray-800 mb-1">Notes Generated</h4>
                          <p className="text-3xl font-bold text-blue-600">{orgData.notesThisMonth}</p>
                          <p className="text-sm text-gray-500 mt-1">
                            Avg: {orgData.activeDoctorsThisMonth > 0
                              ? (orgData.notesThisMonth / orgData.activeDoctorsThisMonth).toFixed(1)
                              : '0'} notes per active doctor
                          </p>
                        </div>
                        <div className="flex flex-col items-center justify-center bg-blue-50 rounded-lg p-3 min-w-[80px]">
                          <div className="text-xs text-blue-600 font-medium text-center mb-2">
                            {orgData.notesThisMonth > 0 ? 'Active' : 'No Notes'}
                          </div>
                          <button
                            onClick={() => setShowInvoiceModal(true)}
                            className="px-3 py-1 text-xs bg-ktx-purple text-white rounded-lg hover:bg-white hover:text-ktx-purple transition-colors font-medium border border-gray-300"
                          >
                            Invoice
                          </button>
                        </div>
                      </div>

                    </div>

                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Analytics</h3>
                <div className="flex flex-wrap gap-3 justify-between">
                  <button
                    onClick={() => setShowLeaderboard("monthly")}
                    className="px-6 py-3 bg-ktx-purple text-white rounded-lg hover:bg-ktx-navy transition-colors font-medium"
                  >
                    📊 Monthly Leaderboard
                  </button>

                  <button
                    onClick={() => setShowLeaderboard("weekly")}
                    className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                  >
                    📈 Weekly Leaderboard
                  </button>

                  <button
                    onClick={handlePatientTypes}
                    className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
                  >
                    👥 Patient Types
                  </button>
                  <button
                    onClick={() => setOrgChartModal(true)}
                    className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium"
                  >
                    📈 Monthly Trends
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex justify-center items-center h-64">
              <div className="text-center">
                <div className="text-gray-400 text-6xl mb-4">📊</div>
                <p className="text-gray-500 text-lg">No data available for the selected month</p>
                <p className="text-gray-400 text-sm">Try selecting a different month</p>
              </div>
            </div>
          )}
        </div>

        {/* Modals */}
        {showLeaderboard === 'monthly' && (
          <CurrentMonthLeaderboard
            leaderboardData={orgData.monthlyLeaderboard}
            org={org}
            setShowLeaderboard={setShowLeaderboard}
            totalDoctors={orgData.totalDoctors}
          />
        )}

        {showLeaderboard === "weekly" && (
          <WeeklyLeaderboard
            org={org}
            doctorsArray={doctorsArr}
            showLeaderboard={showLeaderboard}
            setShowLeaderboard={setShowLeaderboard}
            totalDoctors={orgData.totalDoctors}
          />
        )}

        {openPatientsModal && (
          <PatientTypeBreakdown
            patientTypes={patientTypes}
            setPatientTypes={setPatientTypes}
            setOpenPatientsModal={setOpenPatientsModal}
            org={org}
          />
        )}

        {orgChartModal && (
          <OrgMonthlyChart
            org={org}
            setOpenOrgModal={setOrgChartModal}
          />
        )}

        {showInvoiceModal && (
          <InvoiceModal
            org={org}
            orgData={orgData}
            selectedMonth={selectedMonth}
            setShowInvoiceModal={setShowInvoiceModal}
            setShowNames={setShowNames}
            setShowEmails={setShowEmails}
          />
        )}
      </div>
    </div>
  );
}